import { ref } from 'vue';

import { ExperienceStringTypes } from '@/shared/models/experience-types';

export interface ViewHistoryItem {
  id: number;
  type: string;
  timestamp: number;
}

// Типы опыта, которые можно показать в истории просмотров.
const supportedViewHistoryTypes: string[] = [
  ExperienceStringTypes.TOUR,
  ExperienceStringTypes.EXCURSION,
  ExperienceStringTypes.INDEPENDENT_WALK,
];

// Ключ localStorage — общий контракт с другими микрофронтендами (карточка экскурсии пишет
// сюда просмотры). Менять значение нельзя без синхронного изменения на стороне писателя.
const VIEW_HISTORY_STORAGE_KEY = 'experience_view_history';
// Максимум последних просмотров для блока «Вы недавно смотрели» (карусель).
const VIEW_HISTORY_LIMIT = 24;

// Работает с историей просмотров из localStorage: загрузка с фильтрацией
// по поддержанным типам и сортировка результатов по времени просмотра.
export const useViewHistory = () => {
  const viewHistory = ref<ViewHistoryItem[]>([]);

  const loadViewHistory = () => {
    try {
      const stored = localStorage.getItem(VIEW_HISTORY_STORAGE_KEY);
      if (stored) {
        viewHistory.value = (JSON.parse(stored) as ViewHistoryItem[])
          .filter((item) => supportedViewHistoryTypes.includes(item.type))
          .slice(-VIEW_HISTORY_LIMIT);
      }
    } catch {
      viewHistory.value = [];
    }
  };

  // Сортирует элементы по времени просмотра (от новых к старым).
  // Элементы без записи в истории получают 0 и оказываются в конце.
  const sortByViewedAt = <T extends { id: number }>(items: T[]): T[] => {
    // Создаем Map для быстрого O(1) поиска timestamp по id
    const timestampMap = new Map(viewHistory.value.map((item) => [item.id, item.timestamp]));

    return [...items].sort((a, b) => (timestampMap.get(b.id) ?? 0) - (timestampMap.get(a.id) ?? 0));
  };

  return { viewHistory, loadViewHistory, sortByViewedAt };
};
